import { CreditCard, Search, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  IdentificationModeTabs,
  type IdentificationModeTab,
} from '../identification/IdentificationModeTabs'
import type { IdentificationMode } from './patient-identification-model'

function useSchedulerModes(): ReadonlyArray<IdentificationModeTab<IdentificationMode>> {
  const { t } = useTranslation()
  return [
    { id: 'card', icon: CreditCard, label: t('self-service.identification.mode-card'), testId: 'patient-id-mode-card' },
    { id: 'search', icon: Search, label: t('self-service.identification.mode-search'), testId: 'patient-id-mode-search' },
    { id: 'create', icon: UserPlus, label: t('self-service.identification.mode-create'), testId: 'patient-id-mode-create' },
  ]
}

/**
 * Renders the scheduler patient-identification mode tabs (card, name search,
 * first visit) with the shared kiosk identification tabs.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <SchedulerModeTabs activeMode="card" onModeChange={() => undefined} />
 * ```
 */
export function SchedulerModeTabs({
  activeMode,
  onModeChange,
}: {
  activeMode: IdentificationMode
  onModeChange: (mode: IdentificationMode) => void
}) {
  const { t } = useTranslation()
  return (
    <IdentificationModeTabs
      label={t('self-service.identification.mode-label')}
      modes={useSchedulerModes()}
      activeMode={activeMode}
      onModeChange={onModeChange}
    />
  )
}

/**
 * Renders scheduler identification tabs without kiosk patient creation.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <SchedulerRestrictedModeTabs activeMode="card" onModeChange={() => undefined} />
 * ```
 */
export function SchedulerRestrictedModeTabs({
  activeMode,
  onModeChange,
}: {
  activeMode: IdentificationMode
  onModeChange: (mode: IdentificationMode) => void
}) {
  const { t } = useTranslation()
  const modes = useSchedulerModes().filter(({ id }) => id !== 'create')
  return (
    <IdentificationModeTabs
      label={t('self-service.identification.mode-label')}
      modes={modes}
      activeMode={activeMode}
      onModeChange={onModeChange}
    />
  )
}
